import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchEstudiantesInscritos } from '../../api/estudiantesInscritos';
import { getCampus } from '../../api/campus';
import Sidebar, { useSidebar } from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import { exportReporteEstudiantesInscritosXLS } from '../../utils/exportReporteEstudiantesInscritosXLS';
import layoutStyles from '../../styles/PantallaDeProgramasAcademicos.v2.module.css';
import reportStyles from '../../styles/ReportesIngresosDiarios.module.css';
import estudiantesStyles from '../../styles/EstudiantesStyles/Estudiantes.module.css';

const PAGE_SIZE = 15;

const parseCiclo = (value) => {
  if (!value) return null;
  const normalized = String(value).trim();
  // Acepta formatos como "2026-01", "2026/1" y también texto que lo contenga.
  const match = normalized.match(/(\d{4})\s*[-\/]\s*(\d{1,2})/);
  if (!match) return null;

  const year = Number(match[1]);
  const period = Number(match[2]);
  if (Number.isNaN(year) || Number.isNaN(period)) return null;

  return {
    year,
    period,
    order: year * 100 + period,
    normalized: `${year}-${period}`,
  };
};

const getRowCicloValue = (row) => {
  return row?.nombre_ciclo || row?.ciclo_escolar || row?.id_ciclo_escolar || '';
};

const ReporteEstudiantesInscritosScreen = () => {
  const { isCollapsed } = useSidebar();
  const [filters, setFilters] = useState({
    cicloInicial: '',
    cicloFinal: '',
    campus: '',
    seccion: '',
    programa: '',
  });

  // Datos de la tabla
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const ciclosDisponibles = useMemo(() => {
    const cicloMap = new Map();

    allRows.forEach((row) => {
      const rawValue = getRowCicloValue(row);
      const parsed = parseCiclo(rawValue);
      if (!parsed) return;

      if (!cicloMap.has(parsed.normalized)) {
        cicloMap.set(parsed.normalized, {
          value: parsed.normalized,
          label: parsed.normalized,
          order: parsed.order,
        });
      }
    });

    return Array.from(cicloMap.values()).sort((a, b) => a.order - b.order);
  }, [allRows]);

  const ciclosFinalesDisponibles = useMemo(() => {
    const parsedInicial = parseCiclo(filters.cicloInicial);
    if (!parsedInicial) return ciclosDisponibles;

    return ciclosDisponibles.filter((ciclo) => ciclo.order >= parsedInicial.order);
  }, [ciclosDisponibles, filters.cicloInicial]);

  // Obtener secciones únicas de los datos de la tabla
  const seccionesUnicas = useMemo(() => {
    const seccionMap = new Map();

    allRows.forEach((row) => {
      const nombreSeccion = row?.seccion_nombre;
      if (!nombreSeccion) return;

      const idSeccion = row?.id_seccion;
      const value = idSeccion !== null && idSeccion !== undefined && idSeccion !== ''
        ? String(idSeccion)
        : String(nombreSeccion);

      if (!seccionMap.has(value)) {
        seccionMap.set(value, {
          value,
          label: String(nombreSeccion),
        });
      }
    });

    return Array.from(seccionMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [allRows]);

  const programasUnicos = useMemo(() => {
    const programaMap = new Map();

    allRows.forEach((row) => {
      const idPrograma = row?.id_programa_actual;
      if (idPrograma === null || idPrograma === undefined || idPrograma === '') return;

      const value = String(idPrograma);
      if (!programaMap.has(value)) {
        programaMap.set(value, {
          value,
          label: String(row?.programa_nombre || `Programa ${value}`),
        });
      }
    });

    return Array.from(programaMap.values()).sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [allRows]);

  // Campus para el filtro (sincronizado con el header global)
  const [campusList, setCampusList] = useState([]);

  useEffect(() => {
    let mounted = true;
    getCampus().catch(() => []).then(cam => {
      if (mounted) setCampusList(Array.isArray(cam) ? cam : []);
    });
    return () => { mounted = false; };
  }, []);

  // Sincronizar filters.campus con el campus del header global
  useEffect(() => {
    if (!campusList.length) return;
    const resolve = (name) => {
      const found = campusList.find(c => c.nombre === name || String(c.id) === name);
      setFilters(prev => ({ ...prev, campus: found ? String(found.id) : '' }));
    };
    const handler = (e) => resolve((e.detail?.campus) || localStorage.getItem('selectedCampus') || '');
    window.addEventListener('campusChanged', handler);
    resolve(localStorage.getItem('selectedCampus') || '');
    return () => window.removeEventListener('campusChanged', handler);
  }, [campusList]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    // Los ciclos se filtran localmente para soportar formatos heterogéneos.
    const params = {};
    if (filters.campus) params.campus = filters.campus;
    if (filters.seccion) params.seccion = filters.seccion;
    if (filters.programa) params.programa = filters.programa;
    console.log('Parámetros enviados a la API:', params);
    fetchEstudiantesInscritos(params)
      .then(data => {
        const apiRows = Array.isArray(data) ? data : data.results || [];
        setAllRows(apiRows);
      })
      .catch(err => {
        setError('Error al cargar inscripciones');
      })
      .finally(() => setLoading(false));
  }, [filters.campus, filters.seccion, filters.programa]);

  const handleExportXLS = useCallback(() => {
    if (!rows || rows.length === 0) return;

    exportReporteEstudiantesInscritosXLS({
      resultados: rows,
      cicloInicial: filters.cicloInicial,
      cicloFinal: filters.cicloFinal,
    });
  }, [rows, filters.cicloInicial, filters.cicloFinal]);

  useEffect(() => {
    const parsedInicial = parseCiclo(filters.cicloInicial);
    const parsedFinal = parseCiclo(filters.cicloFinal);

    const filtered = allRows.filter((row) => {
      const parsedRowCiclo = parseCiclo(getRowCicloValue(row));
      if (!parsedRowCiclo) return !parsedInicial && !parsedFinal;

      if (parsedInicial && parsedRowCiclo.order < parsedInicial.order) return false;
      if (parsedFinal && parsedRowCiclo.order > parsedFinal.order) return false;

      return true;
    });

    setRows(filtered);
  }, [allRows, filters.cicloInicial, filters.cicloFinal]);

  // Resetear a página 1 cuando cambian los datos filtrados
  useEffect(() => {
    setCurrentPage(1);
  }, [rows]);

  // Filas de la página actual
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, currentPage]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));

  // Handler para los filtros
  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    if (name === 'cicloInicial') {
      const parsedNuevoInicial = parseCiclo(value);
      const parsedFinalActual = parseCiclo(filters.cicloFinal);

      setFilters((prev) => ({
        ...prev,
        cicloInicial: value,
        cicloFinal:
          parsedNuevoInicial && parsedFinalActual && parsedFinalActual.order < parsedNuevoInicial.order
            ? ''
            : prev.cicloFinal,
      }));
      return;
    }

    setFilters({ ...filters, [name]: value });
  };

  // ── Helpers de paginación ─────────────────────────────────────────────
  const paginationBtnStyle = (disabled, active = false) => ({
    padding: '4px 10px',
    borderRadius: '4px',
    border: '1px solid #c7d2e0',
    background: active ? '#1e3a8a' : disabled ? '#f0f0f0' : '#fff',
    color: active ? '#fff' : disabled ? '#aaa' : '#1e3a8a',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontWeight: active ? '600' : 'normal',
    fontSize: '0.82rem',
    minWidth: '32px',
  });

  const buildPageNumbers = (page, total) => {
    const half = 2;
    let from = Math.max(1, page - half);
    let to   = Math.min(total, page + half);
    if (to - from < 4) {
      if (from === 1) to = Math.min(total, from + 4);
      else from = Math.max(1, to - 4);
    }
    const pages = [];
    for (let i = from; i <= to; i++) pages.push(i);
    return pages;
  };

  return (
    <div className={layoutStyles.mainContainer} style={{ height: '100vh', display: 'flex' }}>
      <div
        className={`${layoutStyles.layout} ${isCollapsed ? layoutStyles.sidebarCollapsedLayout : ''}`}
        style={{ flex: 1, display: 'flex', height: '100vh' }}
      >
        <div className={layoutStyles.sidebarColumn} style={{ height: '100vh', minHeight: 0 }}>
          <Sidebar />
        </div>
        <div
          className={layoutStyles.contentColumn}
          style={{ position: 'relative', height: '100vh', overflowY: 'auto', minHeight: 0 }}
        >
        <HeaderGlobal title="Reporte de Estudiantes" campus={filters.campus} />
          <main className={layoutStyles.mainContent}>
            <div className={layoutStyles.contentArea}>
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Filtros estilo rectoría */}
                <div className={estudiantesStyles.searchSection}>
                  <div className={estudiantesStyles.searchCard}>
                    <div className={estudiantesStyles.searchRow}>
                      {/* Filtro Ciclo Escolar Inicial */}
                      <div className={estudiantesStyles.formGroup}>
                        <label className={estudiantesStyles.searchLabel}>Ciclo Escolar Inicial</label>
                        <select
                          className={estudiantesStyles.searchInput}
                          name="cicloInicial"
                          value={filters.cicloInicial}
                          onChange={handleFilterChange}
                        >
                          <option value="">Todos</option>
                          {ciclosDisponibles.map((ciclo) => (
                            <option key={ciclo.value} value={ciclo.value}>{ciclo.label}</option>
                          ))}
                        </select>
                      </div>
                      {/* Filtro Ciclo Escolar Final */}
                      <div className={estudiantesStyles.formGroup}>
                        <label className={estudiantesStyles.searchLabel}>Ciclo Escolar Final</label>
                        <select
                          className={estudiantesStyles.searchInput}
                          name="cicloFinal"
                          value={filters.cicloFinal}
                          onChange={handleFilterChange}
                        >
                          <option value="">Todos</option>
                          {ciclosFinalesDisponibles.map((ciclo) => (
                            <option key={ciclo.value} value={ciclo.value}>{ciclo.label}</option>
                          ))}
                        </select>
                      </div>
                      {/* Filtro Sección */}
                      <div className={estudiantesStyles.formGroup}>
                        <label className={estudiantesStyles.searchLabel}>Sección</label>
                        <select
                          className={estudiantesStyles.searchInput}
                          name="seccion"
                          value={filters.seccion}
                          onChange={handleFilterChange}
                        >
                          <option value="">Todas</option>
                          {seccionesUnicas.map((seccion) => (
                            <option key={seccion.value} value={seccion.value}>{seccion.label}</option>
                          ))}
                        </select>
                      </div>
                      {/* Filtro Programa */}
                      <div className={estudiantesStyles.formGroup}>
                        <label className={estudiantesStyles.searchLabel}>Programa</label>
                        <select
                          className={estudiantesStyles.searchInput}
                          name="programa"
                          value={filters.programa}
                          onChange={handleFilterChange}
                        >
                          <option value="">Todos</option>
                          {programasUnicos.map((programa) => (
                            <option key={programa.value} value={programa.value}>{programa.label}</option>
                          ))}
                        </select>
                      </div>
                      {/* Limpiar */}
                      <div className={reportStyles.filterActions}>
                        <button
                          type="button"
                          className={reportStyles.clearFiltersButton}
                          onClick={() => setFilters(prev => ({ campus: prev.campus, cicloInicial: '', cicloFinal: '', seccion: '', programa: '' }))}
                        >
                          Limpiar filtros
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={reportStyles.actionsBar}>
                  <button
                    type="button"
                    className={`${reportStyles.downloadButton} bg-blue-900 text-white border-transparent`}
                    onClick={handleExportXLS}
                    disabled={rows.length === 0}
                    title={rows.length > 0 ? 'Descargar Excel' : 'No hay datos para exportar'}
                    style={{
                      backgroundColor: '#1e3a8a',
                      color: '#ffffff',
                      borderColor: 'transparent',
                      opacity: rows.length === 0 ? 0.6 : 1,
                      cursor: rows.length === 0 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <span className={reportStyles.downloadIcon}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 3v12m0 0 4-4m-4 4-4-4"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5 15v3a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span>Descargar Excel</span>
                  </button>
                </div>
                {/* Tabla */}
                <div className={reportStyles.reportContainer}>
                  <table className={reportStyles.dataTable}>
                    <thead>
                      <tr style={{ background: '#e0e7ef' }}>
                        <th>#</th>
                        <th>CICLO ESCOLAR</th>
                        <th>MATRÍCULA</th>
                        <th>NOMBRE</th>
                        <th>SECCIÓN</th>
                        <th>PROGRAMA</th>
                        <th>TIPO DE ESTUDIANTE</th>
                        <th>TURNO</th>
                        <th>MODALIDAD</th>
                        <th>ESTATUS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={10} style={{ textAlign: 'center', color: '#777', fontSize: '0.9rem', padding: '16px' }}>
                            Cargando datos...
                          </td>
                        </tr>
                      ) : error ? (
                        <tr>
                          <td colSpan={10} style={{ textAlign: 'center', color: 'red', fontSize: '0.9rem', padding: '16px' }}>
                            {error}
                          </td>
                        </tr>
                      ) : rows.length === 0 ? (
                        <tr>
                          <td colSpan={10} style={{ textAlign: 'center', color: '#777', fontSize: '0.9rem', padding: '16px' }}>
                            No hay datos para mostrar.
                          </td>
                        </tr>
                      ) : (
                        paginatedRows.map((row, idx) => (
                          <tr key={row.id_inscripcion != null ? `i-${row.id_inscripcion}` : `e-${row.id_estudiante}`}>
                            <td>{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                            <td>{row.nombre_ciclo || '—'}</td>
                            <td>{row.matricula || ''}</td>
                            <td>{row.nombre_completo || ''}</td>
                            <td>{row.seccion_nombre || '—'}</td>
                            <td>{row.programa_nombre || '—'}</td>
                            <td>{row.tipo_estudiante || '—'}</td>
                            <td>{row.turno || '—'}</td>
                            <td>{row.modalidad || '—'}</td>
                            <td>{row.estatus_display || '—'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Paginación */}
                {!loading && rows.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', flexWrap: 'wrap', gap: '8px' }}>
                    <span style={{ fontSize: '0.85rem', color: '#555' }}>
                      Mostrando {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, rows.length)} de {rows.length.toLocaleString('es-MX')} registros
                    </span>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        style={paginationBtnStyle(currentPage === 1)}
                      >«</button>
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        style={paginationBtnStyle(currentPage === 1)}
                      >‹</button>
                      {buildPageNumbers(currentPage, totalPages).map(p => (
                        <button
                          key={p}
                          onClick={() => setCurrentPage(p)}
                          style={paginationBtnStyle(false, p === currentPage)}
                        >{p}</button>
                      ))}
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        style={paginationBtnStyle(currentPage === totalPages)}
                      >›</button>
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        style={paginationBtnStyle(currentPage === totalPages)}
                      >»</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ReporteEstudiantesInscritosScreen;
